'use client';

import { useState, useEffect, useRef } from 'react';

interface TypewriterTextProps {
    text: string;
    delay?: number;
}

function TypewriterText({ text, delay = 50 }: TypewriterTextProps) {
    const [displayedText, setDisplayedText] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        let currentIndex = 0;
        const interval = setInterval(() => {
            if (currentIndex <= text.length) {
                setDisplayedText(text.slice(0, currentIndex));
                currentIndex++;
            } else {
                clearInterval(interval);
            }
        }, delay);

        return () => clearInterval(interval);
    }, [isVisible, text, delay]);

    return (
        <div ref={sectionRef}>
            <span>{displayedText}</span>
            {displayedText.length < text.length && (
                <span className="inline-block w-0.5 h-8 bg-primary ml-1 animate-pulse" />
            )}
        </div>
    );
}

export default function MissionSection() {
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    if (!isHydrated) {
        return (
            <section className="py-20 bg-surface/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="h-64 bg-surface rounded-lg animate-pulse" />
                </div>
            </section>
        );
    }

    return (
        <section className="py-20 bg-surface/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl font-bold text-text-primary mb-6">
                        Our <span className="gradient-text">Mission</span>
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-primary via-accent to-success mx-auto rounded-full" />
                </div>

                <div className="glass glass-border rounded-2xl p-8 sm:p-12 lg:p-16 max-w-5xl mx-auto">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-text-primary leading-relaxed">
                        <TypewriterText
                            text="To transform potential into achievement by creating an educational ecosystem where ancient wisdom meets cutting-edge technology, empowering every learner to reach their fullest potential through personalized, engaging, and measurable learning experiences."
                            delay={30}
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8 mt-16">
                    <div className="glass glass-border rounded-xl p-8 hover-lift transition-smooth">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mb-6 shadow-glow">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-text-primary mb-3">Innovation First</h3>
                        <p className="text-text-secondary leading-relaxed">
                            We continuously push the boundaries of educational technology, integrating AI, analytics, and immersive experiences to create learning that adapts to each individual.
                        </p>
                    </div>

                    <div className="glass glass-border rounded-xl p-8 hover-lift transition-smooth">
                        <div className="w-16 h-16 bg-gradient-to-br from-accent to-success rounded-lg flex items-center justify-center mb-6 shadow-glow">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-text-primary mb-3">Community Driven</h3>
                        <p className="text-text-secondary leading-relaxed">
                            Learning thrives in connection. We foster vibrant communities where students, educators, and institutions collaborate, share, and grow together.
                        </p>
                    </div>

                    <div className="glass glass-border rounded-xl p-8 hover-lift transition-smooth">
                        <div className="w-16 h-16 bg-gradient-to-br from-success to-primary rounded-lg flex items-center justify-center mb-6 shadow-glow">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-text-primary mb-3">Results Focused</h3>
                        <p className="text-text-secondary leading-relaxed">
                            Every feature, every interaction is designed with measurable outcomes in mind. We believe in education that delivers tangible, life-changing results.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}